/**
 * Adventure Validation Panel (Backlog 057), read-only.
 *
 * Lists an adventure's validation items from GET /api/adventures/{id}/validation (User API):
 * open items first, then the most recent resolved ones. Each row links to the ticket in the
 * moderator queue, which is where an item is claimed, worked and resolved. The portal moderates
 * only, so nothing here creates or edits an item.
 *
 * Renders nothing when the API answers 404 (not a manager or moderator, or an API that has not
 * shipped the endpoint yet) or when the adventure has no items at all.
 */

import { useEffect, useState } from 'react'
import { Alert, Box, Chip, CircularProgress, Link, List, ListItem, Paper, Typography } from '@mui/material'
import { FactCheck } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { getAdventureValidation } from '@api/services/tickets.api'
import { AdventureValidationDto, TicketStatus, TicketTypeLabels } from '@api/types/tickets'
import { TicketTypeIcon } from './TicketTypeIcon'
import { TicketStatusBadge } from './TicketStatusBadge'

interface AdventureValidationPanelProps {
  adventureId: string
}

const isOpen = (status: TicketStatus) => status !== TicketStatus.Resolved && status !== TicketStatus.Closed

export function AdventureValidationPanel({ adventureId }: AdventureValidationPanelProps) {
  const [data, setData] = useState<AdventureValidationDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    getAdventureValidation(adventureId)
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load validation items')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [adventureId])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading validation items</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Could not load validation items: {error}
      </Alert>
    )
  }

  if (!data || data.items.length === 0) return null

  const open = data.items.filter((i) => isOpen(i.status))
  const resolved = data.items.filter((i) => !isOpen(i.status))

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <FactCheck fontSize="small" color={data.openCount > 0 ? 'info' : 'disabled'} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, minWidth: 0 }}>
          Validation
        </Typography>
        <Chip
          size="small"
          color={data.openCount > 0 ? 'info' : 'default'}
          label={data.openCount > 0 ? `${data.openCount} ${data.openCount === 1 ? 'ticket' : 'tickets'}` : 'No tickets'}
        />
      </Box>

      {open.length > 0 && <ItemList items={open} />}

      {resolved.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Recently resolved
          </Typography>
          <ItemList items={resolved} muted />
        </>
      )}
    </Paper>
  )
}

function ItemList({ items, muted = false }: { items: AdventureValidationDto['items']; muted?: boolean }) {
  return (
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem
          key={item.ticketId}
          disableGutters
          sx={{ alignItems: 'flex-start', gap: 1, opacity: muted ? 0.7 : 1 }}
        >
          <Box sx={{ pt: 0.25, flexShrink: 0 }}>
            <TicketTypeIcon type={item.type} size="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Link
              component={RouterLink}
              to={`/tickets/${item.ticketId}`}
              variant="body2"
              underline="hover"
              sx={{ wordBreak: 'break-word', display: 'block' }}
            >
              {item.title}
            </Link>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {item.typeLabel || TicketTypeLabels[item.type] || 'Validation'}
              </Typography>
              {item.itemKey && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {item.itemKey}
                </Typography>
              )}
              <TicketStatusBadge status={item.status} />
            </Box>
          </Box>
        </ListItem>
      ))}
    </List>
  )
}
